import { Rows, List } from "@phosphor-icons/react"
import { useQuery } from "@tanstack/react-query"
import {
  type ColumnSizingState,
  type SortingState,
} from "@tanstack/react-table"
import { Button } from "@tokenterminal/core/ui/button/Button"
import {
  ToggleButtonGroup,
  ToggleIconButton,
} from "@tokenterminal/core/ui/input/toggle/ToggleButtonGroup"
import {
  type Preset,
  type PresetColumnConfig,
} from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { ClientOnlySuspense } from "components/ClientOnlySuspense"
import {
  atom,
  useAtom,
  useSetAtom,
  type SetStateAction,
  type WritableAtom,
} from "jotai"
import { useMart } from "modules/mart/hooks/useMart"
import { createConfigAtom } from "modules/state-config/store/createConfigAtom"
import { useMetricsList } from "modules/v3/MetricsAside/useMetricsList"
import { useRouter } from "next/router"
import { Suspense, useMemo, useState, useRef } from "react"
import { useTRPC } from "utils/trpc"
import ColumnSelectorModal from "./ColumnSelectorModal/ColumnSelectorModal"
import DownloadAsCSVButton from "./DownloadAsCSVButton"
import { ChainDropdown } from "./filters/ChainDropdown"
import ColumnFiltersList from "./filters/ColumnFiltersList"
import { MarketSectorDropdown } from "./filters/MarketSectorDropdown"
import { PresetSelector } from "./PresetSelector"
import ScrollToTopButton from "./ScrollToTopButton"
import { Search } from "./Search"
import { TableComponent as Table } from "./Table"

const ResetFiltersButton = ({
  filtersAtom,
  hideMarketSectorDropdown,
  hideChainDropdown,
}: {
  filtersAtom: FiltersAtomType
  hideMarketSectorDropdown: boolean
  hideChainDropdown: boolean
}) => {
  const [filters, setFilters] = useAtom(filtersAtom)

  const handleResetFilters = () => {
    setFilters({
      ...filters,
      filters: [],
      chains: [],
      marketSectors: [],
    })
  }

  if (
    filters.filters.length === 0 &&
    (filters.chains.length === 0 || hideChainDropdown) &&
    (filters.marketSectors.length === 0 || hideMarketSectorDropdown)
  ) {
    return null
  }

  return (
    <Button
      onPress={handleResetFilters}
      variant="ghost"
      size="small"
      className="text-fg-secondary"
    >
      Reset filters
    </Button>
  )
}

export type FiltersAtom = {
  marketSectors: string[]
  chains: string[]
  search: string
  columns: PresetColumnConfig[]
  sorting: SortingState
  columnSizing: ColumnSizingState
  filters: { field: string; operator: string; value: number | number[] }[]
}

// For a better filter editing UX, we allow undefined values in the filters array
export type FiltersAtomWithUndefinedValues = Omit<FiltersAtom, "filters"> & {
  filters: {
    field: string
    operator: string
    value: number | (number | undefined)[] | undefined
  }[]
}

export type FiltersAtomType = WritableAtom<
  FiltersAtom,
  [SetStateAction<FiltersAtom>],
  Promise<void>
>

const DEFAULT_WIDTH = 1680

function usePreset(preset?: string) {
  const trpc = useTRPC()

  const { data } = useQuery(
    trpc.projects.getPresets.queryOptions(
      {
        preset: preset ?? "",
      },
      {
        enabled: !!preset,
      }
    )
  )

  return data ? data : null
}

const formattedDisplay = (display: string) => {
  let formattedValue = ""
  const [value] = display.split(" ")

  if (["avg", "sum", "change"].includes(display)) {
    return `${display}`
  }

  if (display.includes("avg")) {
    formattedValue += `Avg. ${value}`
  } else if (display.includes("sum")) {
    formattedValue += value
  } else if (display.includes("change")) {
    formattedValue += `Change ${value}`
  } else if (display.includes("trend")) {
    formattedValue += `Trend ${value}`
  } else if (display.includes("ATH")) {
    formattedValue += `ATH`
  } else if (display.includes("ATL")) {
    formattedValue += `ATL`
  } else {
    formattedValue += "Latest"
  }

  return formattedValue.trim()
}

export default function ScreenerTable({
  presetOverride,
  chain_id,
  disableUrlState,
  header,
  showPresetSelector = true,
  hideMarketSectorDropdown = false,
  hideEditColumns = false,
  ignoreThreshold = false,
  hideColumnFilters = false,
}: {
  presetOverride?: Preset
  chain_id?: string
  disableUrlState?: boolean
  header?: React.ReactNode
  showPresetSelector?: boolean
  hideMarketSectorDropdown?: boolean
  hideEditColumns?: boolean
  ignoreThreshold?: boolean
  hideColumnFilters?: boolean
}) {
  const defaultPreset = useMart("crypto_screener", false)
  const [selectedPreset, setSelectedPreset] = useState("overview")
  const preset = usePreset(selectedPreset)
  const [compact, setCompact] = useState(false)
  const router = useRouter()
  const metrics = useMetricsList()
  const filtersContainerRef = useRef<HTMLDivElement>(null)

  const currentPreset = presetOverride ?? preset

  const filtersAtom = useMemo(() => {
    return createConfigAtom<FiltersAtom>(
      {
        marketSectors: currentPreset?.filters?.market_sectors ?? [],
        chains: currentPreset?.filters?.chains ?? [],
        search: "",
        columns:
          currentPreset?.columns.map((c) => ({
            ...c,
            field: c.field === "market_sector_id" ? "market_sectors" : c.field,
          })) ?? [],
        sorting: currentPreset?.filters?.sort ?? [
          {
            id: "fees_30d_sum",
            desc: true,
          },
        ],
        columnSizing: currentPreset?.filters?.column_sizing ?? {},
        filters: currentPreset?.filters?.filters ?? [],
      },
      "_project_filters",
      "terminal",
      disableUrlState
    )
  }, [currentPreset, disableUrlState])

  const columns = useMemo(() => {
    const defaultColumns =
      defaultPreset?.column_definitions
        .filter((column) => column.parent !== "" && column.parent !== null)
        .map((column: any) => {
          const group = defaultPreset?.column_definitions.find(
            (c) => c.col_id === column.parent
          )?.header_name

          const metricId = column.parent?.split("_group")[0]
          const metric =
            metrics.data?.find((m) => m.metric_id === metricId) ||
            (metricId === "price" ? { slug: "price" } : undefined)

          const display = formattedDisplay(column.header_name)

          const length = Math.max(
            group?.length ?? 0,
            display.length,
            column.field.length
          )

          const size = Math.round(40 * Math.pow(length, 0.5))

          return {
            field: column.field,
            display,
            header: column.header_name,
            cell_data_type: column.cell_data_type,
            group: group,
            metric_slug: metric?.slug,
            label: `${group} (${column.header_name})`,
            default_size: size,
          }
        }) ?? []

    return [
      {
        header: "Market sector",
        field: "market_sectors",
        display: "Market sector",
        default_size: 200,
      },
      {
        header: "Definition",
        field: "definition",
        display: "Project-specific",
        group: "Definition",
        default_size: 250,
      },
      {
        header: "Chains",
        field: "chains",
        display: "Chains",
        default_size: 100,
      },
      {
        header: "Chain",
        field: "chain",
        display: "Chain",
        default_size: 100,
      },
      {
        header: "Listing date",
        field: "listing_date",
        display: "Listing date",
        default_size: 140,
      },
      ...defaultColumns,
    ] as PresetColumnConfig[]
  }, [defaultPreset, metrics])

  const tableContainerRef = useRef<HTMLDivElement>(null)

  const resetFilters = useSetAtom(
    useMemo(() => {
      const resetFiltersAtom = atom(null, (get, set) => {
        set(filtersAtom, {
          marketSectors: currentPreset?.filters?.market_sectors ?? [],
          chains: currentPreset?.filters?.chains ?? [],
          search: "",
          columns: currentPreset?.columns ?? [],
          sorting: currentPreset?.filters?.sort ?? [
            {
              id: "fees_30d_sum",
              desc: true,
            },
          ],
          columnSizing: currentPreset?.filters?.column_sizing ?? {},
          filters: currentPreset?.filters?.filters ?? [],
        })
      })

      return resetFiltersAtom
    }, [filtersAtom, currentPreset])
  )

  const hideChainDropdown = !!chain_id

  return (
    <>
      <div
        className="flex flex-col gap-6 max-w-full w-full mx-auto"
        style={{ maxWidth: DEFAULT_WIDTH }}
      >
        <div className="flex flex-col gap-3 w-full">
          <div className="flex flex-col gap-8 max-w-full w-full">
            {header}
            {showPresetSelector && (
              <div className="flex gap-4 flex-wrap justify-between items-center">
                <PresetSelector
                  presets={[
                    { slug: "overview", name: "Overview" },
                    { slug: "valuation", name: "Valuation" },
                    { slug: "usage", name: "Usage" },
                    { slug: "tvl", name: "TVL" },
                    { slug: "cashflow", name: "Cashflow" },
                    { slug: "technical", name: "Technical" },
                    { slug: "dex", name: "DEX" },
                    { slug: "lending", name: "Lending" },
                    { slug: "stablecoins", name: "Stablecoins" },
                    { slug: "l2s", name: "L2s" },
                    { slug: "l1s", name: "L1s" },
                  ]}
                  selectedPreset={selectedPreset}
                  setSelectedPreset={async (preset) => {
                    await router.push(`/projects`, undefined, { shallow: true })
                    setSelectedPreset(preset)

                    tableContainerRef.current?.scrollTo({
                      top: 0,
                      left: 0,
                      behavior: "instant",
                    })
                  }}
                />
                {/* {params.get("v") && (
                  <button
                    onClick={resetFilters}
                    title="Reset filters"
                    className="text-sm"
                  >
                    Reset filters
                  </button>
                )} */}
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center justify-between">
            <Search filtersAtom={filtersAtom} />
            <div className="flex gap-1 items-center justify-between">
              {!hideEditColumns && (
                <ColumnSelectorModal
                  columns={columns}
                  filtersAtom={filtersAtom}
                />
              )}
              <ToggleButtonGroup
                selectedKey={compact ? "Compact" : "Default"}
                onSelectionChange={(key) => {
                  setCompact(key === "Compact")
                }}
                className="flex-shrink-0"
              >
                <ToggleIconButton id="Default" icon={Rows} />
                <ToggleIconButton id="Compact" icon={List} />
              </ToggleButtonGroup>
              <DownloadAsCSVButton
                filtersAtom={filtersAtom}
                columns={columns}
              />
            </div>
          </div>

          {(!hideColumnFilters ||
            !hideMarketSectorDropdown ||
            !hideChainDropdown) && (
            <div className="flex gap-4 justify-between">
              <div
                ref={filtersContainerRef}
                className="flex gap-2 items-center flex-wrap"
              >
                {!hideMarketSectorDropdown && (
                  <ClientOnlySuspense>
                    <MarketSectorDropdown filtersAtom={filtersAtom} />
                  </ClientOnlySuspense>
                )}
                {!hideChainDropdown && (
                  <ClientOnlySuspense>
                    <ChainDropdown filtersAtom={filtersAtom} />
                  </ClientOnlySuspense>
                )}
                {!hideColumnFilters && (
                  <ClientOnlySuspense>
                    <ColumnFiltersList
                      filtersAtom={filtersAtom}
                      columns={columns}
                      filtersContainerRef={filtersContainerRef}
                    />
                  </ClientOnlySuspense>
                )}
              </div>
              <ClientOnlySuspense>
                <ResetFiltersButton
                  filtersAtom={filtersAtom}
                  hideMarketSectorDropdown={hideMarketSectorDropdown}
                  hideChainDropdown={hideChainDropdown}
                />
              </ClientOnlySuspense>
            </div>
          )}
        </div>
        <Suspense>
          <Table
            filtersAtom={filtersAtom}
            columns={columns}
            compact={compact}
            tableContainerRef={tableContainerRef}
            filterItemId={chain_id}
            ignoreThreshold={ignoreThreshold}
          />
        </Suspense>
      </div>
      <ScrollToTopButton />
    </>
  )
}
